import { useCallback, useEffect, useMemo, useState, type FC } from 'react';
import { Chatbot } from '../../core/chatbot';
import type { AppliedChatbotConfig } from '../../core/types';
import { ChatbotContext } from './Chatbot.context';
import type { ChatbotProviderProps } from './Chatbot.types';

export const ChatbotProvider: FC<ChatbotProviderProps> = ({ logger, children, ...config }) => {
  // Create a single chatbot instance using stable dependencies to prevent re-creation
  const chatbot = useMemo(() => {
    return new Chatbot({
      config,
      logger,
    });
  }, [JSON.stringify(config), logger]);

  // Reactive state to track chatbot status
  const [isMounted, setIsMounted] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Poll for mount state changes since no mount/unmount events exist
  useEffect(() => {
    const checkMountState = () => setIsMounted(chatbot.isMounted());
    checkMountState();
    const pollInterval = setInterval(checkMountState, 500);
    return () => clearInterval(pollInterval);
  }, [chatbot]);

  // Subscribe to open/close events once chatbot is mounted
  useEffect(() => {
    if (!isMounted) {
      setIsOpen(false);
      return;
    }

    const setupEventListeners = () => {
      // Check if Applied Labs events API is available
      // TODO: is not using chatbot.subscribeToEvent because it's not ready yet in that moment
      if (!window.applied?.events?.subscribeEvent) return null;

      // Set up actual event listeners
      const openSubscription = chatbot.subscribeToEvent('applied:chat:open', () => {
        setIsOpen(true);
        setIsLoading(false);
      });

      const closeSubscription = chatbot.subscribeToEvent('applied:chat:close', () => {
        setIsOpen(false);
        setIsLoading(false);
      });

      return { openSubscription, closeSubscription };
    };

    // Try to set up listeners immediately
    let subscriptions = setupEventListeners();
    let retryInterval: NodeJS.Timeout | null = null;

    // If events aren't ready, retry every 500ms until they are
    if (!subscriptions) {
      retryInterval = setInterval(() => {
        subscriptions = setupEventListeners();
        if (subscriptions) {
          clearInterval(retryInterval!);
          retryInterval = null;
        }
      }, 500);
    }

    // Cleanup function to remove subscriptions and stop retrying
    return () => {
      if (retryInterval) clearInterval(retryInterval);
      if (subscriptions) {
        chatbot.unsubscribeFromEvent(subscriptions.openSubscription);
        chatbot.unsubscribeFromEvent(subscriptions.closeSubscription);
      }
    };
  }, [chatbot, isMounted]);

  // Enhanced mount method with loading state
  const mount = useCallback(
    (config?: Partial<AppliedChatbotConfig>) => {
      setIsLoading(true);
      chatbot.mount(config);
    },
    [chatbot],
  );

  // Enhanced unmount method with state cleanup
  const unmount = useCallback(() => {
    setIsLoading(true);
    setIsOpen(false);
    chatbot.unmount();
  }, [chatbot]);

  // Direct chatbot methods
  const show = useCallback(() => chatbot.show(), [chatbot]);
  const hide = useCallback(() => chatbot.hide(), [chatbot]);

  // Enhanced setOpen method with loading state
  const setOpen = useCallback(
    (open: boolean) => {
      setIsLoading(true);
      chatbot.setOpen(open);
    },
    [chatbot],
  );

  // Helper to subscribe to open events with validation
  const onOpen = useCallback(
    (callback: () => void) => {
      // Test if events are ready by attempting a subscription
      const testId = chatbot.subscribeToEvent('applied:chat:open', () => {});
      if (testId === -1) return null; // Events not ready

      // Clean up test and create actual subscription
      chatbot.unsubscribeFromEvent(testId);
      const id = chatbot.subscribeToEvent('applied:chat:open', callback);
      return () => chatbot.unsubscribeFromEvent(id);
    },
    [chatbot],
  );

  // Helper to subscribe to close events with validation
  const onClose = useCallback(
    (callback: () => void) => {
      // Test if events are ready by attempting a subscription
      const testId = chatbot.subscribeToEvent('applied:chat:close', () => {});
      if (testId === -1) return null; // Events not ready

      // Clean up test and create actual subscription
      chatbot.unsubscribeFromEvent(testId);
      const id = chatbot.subscribeToEvent('applied:chat:close', callback);
      return () => chatbot.unsubscribeFromEvent(id);
    },
    [chatbot],
  );

  // Immediate callbacks for mount state
  const onMount = useCallback(
    (callback: () => void) => {
      if (isMounted) callback();
    },
    [isMounted],
  );

  const onUnmount = useCallback(
    (callback: () => void) => {
      if (!isMounted) callback();
    },
    [isMounted],
  );

  // Memoized context value to prevent unnecessary re-renders
  const contextValue = useMemo(
    () => ({
      // Core chatbot methods
      injectScript: () => chatbot.injectScript(),
      injectElement: () => chatbot.injectElement(),
      setMetadata: chatbot.setMetadata.bind(chatbot),
      updateMetadata: chatbot.updateMetadata.bind(chatbot),
      onMessage: chatbot.onMessage.bind(chatbot),
      subscribeToEvent: chatbot.subscribeToEvent.bind(chatbot),
      unsubscribeFromEvent: chatbot.unsubscribeFromEvent.bind(chatbot),
      fireEvent: chatbot.fireEvent.bind(chatbot),
      trackEvent: chatbot.trackEvent.bind(chatbot),

      // Reactive state
      isMounted,
      isOpen,
      isLoading,

      // Enhanced methods with state management
      mount,
      unmount,
      show,
      hide,
      setOpen,

      // Event subscription helpers
      onOpen,
      onClose,
      onMount,
      onUnmount,
    }),
    [
      chatbot,
      isMounted,
      isOpen,
      isLoading,
      mount,
      unmount,
      show,
      hide,
      setOpen,
      onOpen,
      onClose,
      onMount,
      onUnmount,
    ],
  );

  return <ChatbotContext.Provider value={contextValue}>{children}</ChatbotContext.Provider>;
};
